import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { formatApiMessage } from '@/api/client';
import { organizationsApi } from '@/api/organizations.api';
import { AppShell } from '@/components/layout/AppShell';
import { applyBrandColor } from '@/lib/brand';
import { useAuthStore } from '@/stores/auth.store';

const PRESET_COLORS = [
  '#0F2A47', // navy (default)
  '#1E40AF', // royal blue
  '#0F766E', // teal
  '#15803D', // forest
  '#B91C1C', // red
  '#7C3AED', // violet
  '#1F2937', // slate
  '#0369A1', // sky
];

export function BrandingPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canEdit = user?.role === 'owner' || user?.role === 'admin';

  const { data: org, isLoading } = useQuery({
    queryKey: ['organizations', 'me'],
    queryFn: () => organizationsApi.me(),
  });

  const [name, setName] = useState('');
  const [brandColor, setBrandColor] = useState('#0F2A47');
  const [logoUrl, setLogoUrl] = useState('');

  useEffect(() => {
    if (org) {
      setName(org.name);
      setBrandColor(org.brandColor);
      setLogoUrl(org.logoUrl ?? '');
    }
  }, [org]);

  const save = useMutation({
    mutationFn: () =>
      organizationsApi.update({
        name: name.trim() || undefined,
        brandColor,
        logoUrl: logoUrl.trim() || null,
      }),
    onSuccess: (next) => {
      toast.success(t('settings_branding.saved'));
      qc.setQueryData(['organizations', 'me'], next);
      applyBrandColor(next.brandColor);
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  return (
    <AppShell>
      <div className="mb-6">
        <h1 className="page-title">{t('settings_branding.title')}</h1>
        <p className="page-subtitle">{t('settings_branding.subtitle')}</p>
      </div>

      {isLoading ? (
        <div className="card text-center py-12 text-muted">{t('common.loading')}</div>
      ) : !org ? null : (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
          <div className="card space-y-5">
            <div>
              <label className="label">{t('settings_branding.org_name')}</label>
              <input
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={!canEdit}
                maxLength={200}
              />
              <p className="text-[11px] text-muted mt-1">
                {t('settings_branding.org_name_hint', { name: name || t('settings_branding.your_org') })}
              </p>
            </div>

            <div>
              <label className="label">{t('settings_branding.primary_color')}</label>
              <div className="flex items-center gap-3 flex-wrap">
                <input
                  type="color"
                  value={brandColor}
                  onChange={(e) => setBrandColor(e.target.value)}
                  disabled={!canEdit}
                  className="h-10 w-16 rounded cursor-pointer border border-border"
                />
                <input
                  className="input max-w-[120px]"
                  value={brandColor}
                  onChange={(e) => setBrandColor(e.target.value)}
                  disabled={!canEdit}
                  maxLength={7}
                  pattern="^#[0-9a-fA-F]{6}$"
                />
                <div className="flex gap-1.5 flex-wrap">
                  {PRESET_COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => canEdit && setBrandColor(c)}
                      disabled={!canEdit}
                      className="w-7 h-7 rounded-full border-2 border-white shadow-sm"
                      style={{ background: c, outline: brandColor === c ? `2px solid ${c}` : 'none', outlineOffset: 1 }}
                      title={c}
                    />
                  ))}
                </div>
              </div>
              <p className="text-[11px] text-muted mt-2">
                {t('settings_branding.color_hint')}
              </p>
            </div>

            <div>
              <label className="label">
                {t('settings_branding.logo_url')} <span className="text-muted font-normal">{t('common.optional')}</span>
              </label>
              <input
                className="input"
                placeholder="https://..."
                value={logoUrl}
                onChange={(e) => setLogoUrl(e.target.value)}
                disabled={!canEdit}
                maxLength={500}
              />
              <p className="text-[11px] text-muted mt-1">
                {t('settings_branding.logo_hint')}
              </p>
            </div>

            {canEdit ? (
              <div className="pt-3 border-t border-border flex justify-end">
                <button
                  className="btn btn-primary"
                  onClick={() => save.mutate()}
                  disabled={save.isPending}
                >
                  {save.isPending ? t('common.saving') : t('settings_profile.save_changes')}
                </button>
              </div>
            ) : (
              <div className="text-[12px] text-muted border-t border-border pt-3">
                {t('settings_branding.no_permission')}
              </div>
            )}
          </div>

          <aside>
            <div className="card !p-0 overflow-hidden">
              <div className="px-4 py-3 border-b border-border text-[11px] uppercase tracking-wide font-semibold text-muted">
                {t('settings_branding.preview')}
              </div>
              <div className="p-4 space-y-3">
                <div className="flex items-center gap-2.5">
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt=""
                      className="h-8 w-8 rounded-md object-cover"
                      onError={(e) => ((e.currentTarget as HTMLImageElement).style.display = 'none')}
                    />
                  ) : (
                    <div
                      className="h-8 w-8 rounded-md text-white flex items-center justify-center text-[13px] font-bold"
                      style={{ background: brandColor }}
                    >
                      {(name[0] ?? 'E').toUpperCase()}
                    </div>
                  )}
                  <strong className="text-[14px] text-gray-900">{name || t('settings_branding.your_org')}</strong>
                </div>
                <div className="text-[12px] text-gray-700">
                  {t('settings_branding.preview_text')}
                </div>
                <button
                  type="button"
                  className="w-full text-white font-semibold rounded-md py-2 text-[13px]"
                  style={{ background: brandColor }}
                >
                  {t('settings_branding.preview_btn')}
                </button>
                <a
                  href="#"
                  onClick={(e) => e.preventDefault()}
                  className="text-[12px] block text-center"
                  style={{ color: brandColor }}
                >
                  {t('auth.forgot_password')}
                </a>
              </div>
            </div>
          </aside>
        </div>
      )}
    </AppShell>
  );
}
